import { AppWindow, ExternalLink, Loader2, MonitorPlay, PanelsTopLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useScreenConnectCi, useScreenConnectConnections, useScreenConnectDevice, type ScSession } from '@/api/screenconnect';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';

/**
 * ScreenConnect in context: Join buttons on the RMM device page, Device Orbit and PSA tickets.
 * The links open the ScreenConnect Host page in the technician's own browser (their own login):
 * Join connects straight away; Join backstage opens "Join with Options" with Backstage pre-selected
 * (one more click — a link can't choose the logon session). Cards render nothing when ScreenConnect
 * isn't configured or nothing matches.
 */

const header = (title: string, right?: React.ReactNode) => (
  <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
    {right}
  </div>
);

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right text-[13px] text-foreground">{children}</div>
    </div>
  );
}

export function OnlineDot({ s }: { s: Pick<ScSession, 'online' | 'hostConnected'> }) {
  return (
    <span className={cn('whitespace-nowrap text-xs', s.online ? 'text-success' : 'text-muted-foreground')}>
      {s.online ? '● online' : '○ offline'}{s.hostConnected && <span className="ml-1 text-warning">· tech connected</span>}
    </span>
  );
}

/**
 * The three ScreenConnect actions, used everywhere (ticket header box, device card, Remote access rows):
 *  - **Open session** — the Host page with the session selected, no join (PAM requests, commands, messages).
 *    Always enabled: the Host page is useful for an offline machine too.
 *  - **Join session** — joins straight away. Its icon carries the brand colour (the only emphasis).
 *  - **Join backstage** — "Join with Options" with Backstage pre-selected; then click Join Session.
 * Both Join buttons are always shown and greyed out while the machine is offline.
 * `compact` = short labels (table rows); labels also shorten on narrow screens.
 */
export function SessionButtons({ s, compact = false }: { s: ScSession; compact?: boolean }) {
  const btn = 'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded px-2 text-xs font-medium transition-colors';
  const one = (href: string, label: string, short: string, Icon: typeof MonitorPlay, opts: { enabled: boolean; brand?: boolean; title: string }) => {
    const content = (
      <>
        <Icon className={cn('h-3.5 w-3.5', opts.enabled && opts.brand ? 'text-brand-strong dark:text-brand' : 'text-muted-foreground')} />
        {compact ? <span>{short}</span> : <><span className="hidden sm:inline">{label}</span><span className="sm:hidden">{short}</span></>}
      </>
    );
    return opts.enabled ? (
      <a href={href} target="_blank" rel="noreferrer" title={opts.title} aria-label={label}
        className={cn(btn, 'text-foreground hover:bg-accent hover:text-accent-foreground')}>{content}</a>
    ) : (
      <button type="button" disabled title="The device is offline in ScreenConnect" aria-label={`${label} (device offline)`}
        className={cn(btn, 'cursor-not-allowed text-muted-foreground opacity-50')}>{content}</button>
    );
  };
  return (
    <span className="inline-flex items-center gap-0.5">
      {one(s.hostUrl, 'Open session', 'Open', AppWindow, { enabled: true, title: `Open ${s.name} in the ScreenConnect Host page without joining` })}
      {one(s.joinUrl, 'Join session', 'Join', MonitorPlay, { enabled: s.online, brand: true, title: `Join ${s.name} in ScreenConnect` })}
      {one(s.backstageUrl, 'Join backstage', 'Backstage', PanelsTopLeft, {
        enabled: s.online,
        title: s.backstage ? 'Opens Join with Options with Backstage selected, then click Join Session' : 'Opens Join with Options (this machine has not reported a backstage session)',
      })}
    </span>
  );
}

/** The outlined "ScreenConnect" box: online dot + label, a divider, then the three actions. */
export function SessionBox({ s, label = true }: { s: ScSession; label?: boolean }) {
  return (
    <div className="inline-flex h-8 items-center rounded-md border border-input bg-background pl-2.5 pr-1">
      <span className="flex items-center gap-1.5" title={`${s.name} is ${s.online ? 'online' : 'offline'} in ScreenConnect`}>
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', s.online ? 'bg-success' : 'bg-muted-foreground/40')} aria-hidden />
        {label && <span className="hidden text-[10px] font-semibold uppercase tracking-wider text-muted-foreground md:inline">ScreenConnect</span>}
      </span>
      <span className="mx-2 h-4 w-px bg-border" aria-hidden />
      <SessionButtons s={s} />
    </div>
  );
}

function Connections({ sessionId }: { sessionId: string }) {
  const q = useScreenConnectConnections(sessionId);
  if (q.isLoading) return <div className="flex items-center gap-2 border-t border-divider px-4 py-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading connections…</div>;
  const items = q.data?.items ?? [];
  if (q.error || items.length === 0) return null;
  return (
    <div className="border-t border-divider px-4 py-2">
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Recent technician connections</div>
      <ul className="space-y-0.5">
        {items.slice(0, 5).map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-2 text-xs">
            <span className="truncate text-foreground">{c.technician}</span>
            <span className="shrink-0 text-muted-foreground" title={c.start ?? ''}>{fmtRelative(c.start)}{c.minutes != null && ` · ${c.minutes < 1 ? '<1' : Math.round(c.minutes)} min`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SessionBody({ s, showConnections, showJoin = true }: { s: ScSession; showConnections: boolean; showJoin?: boolean }) {
  return (
    <>
      {showJoin && <div className="px-4 pb-3 pt-1"><SessionBox s={s} label={false} /></div>}
      <Row label="Machine"><span className="font-medium">{s.name}</span><div><OnlineDot s={s} /></div></Row>
      {s.loggedOnUser && <Row label="Logged on">{s.loggedOnUser}</Row>}
      <Row label="Last connected">{fmtRelative(s.lastConnected)}</Row>
      {s.clientVersion && <Row label="Client"><span className="font-mono text-xs">{s.clientVersion}</span></Row>}
      {showConnections && <Connections sessionId={s.id} />}
    </>
  );
}

/** RMM device page / Device Orbit: the ScreenConnect session(s) matched to this Datto device. */
export function ScreenConnectDeviceCard({ uid }: { uid: string }) {
  const q = useScreenConnectDevice(uid);
  if (q.error || q.data?.configured === false) return null;
  if (q.isLoading) {
    return <section className="rounded-lg border border-border bg-card shadow-sm">{header('ScreenConnect')}<div className="flex items-center gap-2 px-4 pb-3 pt-1 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding the session…</div></section>;
  }
  const [s, ...others] = q.data?.sessions ?? [];
  if (!s) {
    return <section className="rounded-lg border border-border bg-card shadow-sm">{header('ScreenConnect')}<div className="px-4 pb-3 pt-1 text-xs text-muted-foreground">No ScreenConnect session matches this device (by serial, MAC or hostname).</div></section>;
  }
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      {header('ScreenConnect', <span className="text-[11px] text-muted-foreground">matched by {s.rmm?.matchedBy}</span>)}
      <SessionBody s={s} showConnections />
      {others.length > 0 && <div className="border-t border-divider px-4 py-2 text-[11px] text-warning">{others.length + 1} ScreenConnect sessions match this device (re-installed?). Showing the most recent.</div>}
    </section>
  );
}

/** PSA ticket side rail: the session's details (the Join buttons are in the ticket header, `TicketRemoteActions`). */
export function ScreenConnectTicketCard({ ciId }: { ciId: number | null }) {
  const q = useScreenConnectCi(ciId);
  if (!ciId || q.error || q.isLoading || !q.data?.screenconnect) return null;
  const s = q.data.sessions[0];
  if (!s) return null;
  return (
    <section className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
      {header('ScreenConnect')}
      <SessionBody s={s} showConnections={false} showJoin={false} />
    </section>
  );
}

/**
 * PSA ticket header: the ScreenConnect box (Open session · Join session · Join backstage, see `SessionButtons`)
 * and "Open in Datto RMM" (when the ticket's CI is a Datto device).
 */
export function TicketRemoteActions({ ciId }: { ciId: number | null }) {
  const q = useScreenConnectCi(ciId);
  if (!ciId || !q.data) return null;
  const { rmm, sessions } = q.data;
  const s = sessions[0];
  if (!rmm && !s) return null;
  // Order in the header: ScreenConnect box · Open in Datto RMM · (Open in Autotask, rendered by the page).
  return (
    <>
      {s && <SessionBox s={s} />}
      {rmm && (
        <Button asChild variant="outline" size="sm">
          <a href={rmm.portalUrl} target="_blank" rel="noreferrer" aria-label="Open in Datto RMM" title={`Open ${rmm.hostname} in Datto RMM`}>
            <ExternalLink className="h-3.5 w-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Open in Datto RMM</span>
          </a>
        </Button>
      )}
    </>
  );
}
